/**
 * presale-builder-v5.js — forked from presale-builder-v4.js
 *
 * v5 changes vs v4:
 *   - Bottle selection is always-live: a type (Glass/Steel) + color is
 *     pre-selected on load and updates immediately on click — no separate
 *     "Add" confirmation step, no is-ready gating on the bottle. CTA is
 *     always enabled (refills remain optional add-ons, same as v4).
 *   - Bottle type cards and their color-circle rows are separate elements
 *     (color rows are siblings, matched by data-type / data-color-row-for),
 *     not nested pills inside each card like v2.
 *   - New: blend accordion for refills (one open panel at a time) with a
 *     selected-count badge on collapsed blend tabs.
 *   - New: carousel thumbnail click swaps the active square hero slide.
 */

(function () {
  'use strict';

  // ── State ────────────────────────────────────────────────────────
  var state = {
    bottleType:       '',
    bottleVariantId:  null,
    bottlePrice:      0,
    bottleTitle:      '',
    bottleTypeLabel:  '',
    bottleImage:      '',
    bottleColorLabel: '',
    bottleColorClass: '',
    bottleBg:         '',
    refills:          {},   // { variantId: { qty, price, title, image, blend } }
  };

  var pdpCache = {}; // handle → fetched HTML

  var DISCOUNT            = 0.85; // 15% off
  var DISCOUNT_PCT        = 'PRESALE15';
  var DISCOUNT_SHIP       = 'PRESALEFREE';
  var SERVINGS_PER_REFILL = 5;

  // ── DOM refs ─────────────────────────────────────────────────────
  var dom = {
    bottleTypeCards:     null,
    colorRows:           null,
    refillCards:         null,
    blendTabs:           null,
    blendPanels:         null,
    summaryList:         null,
    summaryInline:       null,
    section:             null,
    mobileFooter:        null,
    mobileBottleLabel:   null,
    mobileBottleSwatch:  null,
    mobileRefillsRow:    null,
    mobileRefillsText:   null,
    removeIconSvg:       '',
  };

  // ── Init ─────────────────────────────────────────────────────────
  function init() {
    dom.bottleTypeCards = document.querySelectorAll('.spb-v5-bottle-type-card');
    dom.colorRows       = document.querySelectorAll('.spb-v5-color-row');
    dom.refillCards     = document.querySelectorAll('.spb-refill-card');
    dom.blendTabs       = document.querySelectorAll('.spb-v5-blend-tab');
    dom.blendPanels     = document.querySelectorAll('.spb-v5-blend-panel');
    dom.summaryList     = document.getElementById('spb-summary-list');
    dom.summaryInline   = document.getElementById('spb-summary-inline');
    dom.section         = document.querySelector('.fb-section--v5');

    dom.mobileFooter       = document.getElementById('fb-mobile-footer');
    dom.mobileBottleLabel  = document.querySelector('[data-fb-mobile-bottle-label]');
    dom.mobileBottleSwatch = document.querySelector('[data-fb-mobile-bottle-swatch]');
    dom.mobileRefillsRow   = document.getElementById('fb-mobile-refills-row');
    dom.mobileRefillsText  = document.querySelector('[data-fb-mobile-refills-text]');

    if (dom.section && dom.mobileFooter) {
      var footerObserver = new IntersectionObserver(function (entries) {
        dom.mobileFooter.classList.toggle('is-visible', entries[0].isIntersecting);
      }, { threshold: 0, rootMargin: '0px 0px -150px 0px' });
      footerObserver.observe(dom.section);
    }

    var iconTpl = document.getElementById('spb-icon-remove');
    if (iconTpl) dom.removeIconSvg = iconTpl.innerHTML.trim();

    // ── Bottle type card click — switches active type ──────────────
    dom.bottleTypeCards.forEach(function (card) {
      card.addEventListener('click', function () {
        selectBottleType(card);
      });
    });

    // ── Color circle click ──────────────────────────────────────────
    document.addEventListener('click', function (e) {
      var circle = e.target.closest('.spb-v5-color-circle');
      if (!circle) return;
      e.stopPropagation();
      if (circle.disabled || circle.classList.contains('is-sold-out')) return;

      var forType = circle.dataset.forType;
      var card = document.querySelector('.spb-v5-bottle-type-card[data-type="' + forType + '"]');
      if (!card) return;

      // If the color belongs to a type that isn't active yet, activate it first
      if (card.dataset.type !== state.bottleType) selectBottleType(card, { skipRender: true });

      var row = document.querySelector('.spb-v5-color-row[data-color-row-for="' + forType + '"]');
      if (row) {
        row.querySelectorAll('.spb-v5-color-circle').forEach(function (c) { c.classList.remove('is-active'); });
        circle.classList.add('is-active');
      }

      // Swap card image + background
      var img   = card.querySelector('.spb-btl-img');
      var inner = card.querySelector('.spb-btl-inner');
      if (img)   img.src = circle.dataset.imageFull || circle.dataset.image;
      if (inner) inner.style.background = circle.dataset.bg;

      card.dataset.variantId  = circle.dataset.variantId;
      card.dataset.price      = circle.dataset.price;
      card.dataset.image      = circle.dataset.image;
      card.dataset.subtext    = circle.dataset.subtext;
      card.dataset.colorClass = circle.dataset.colorClass;
      card.dataset.bg         = circle.dataset.bg;

      applyBottleFromCard(card);
      render();
    });

    // Refill qty buttons
    dom.refillCards.forEach(function (card) {
      var cardInner = card.querySelector('.card__inner');
      var addBtn    = card.querySelector('[data-spb-add]');
      var minus     = card.querySelector('[data-spb-dec]');
      var plus      = card.querySelector('[data-spb-inc]');

      if (cardInner) {
        cardInner.addEventListener('click', function () {
          if (!card.classList.contains('is-active')) changeRefillQty(card, +1);
        });
      }
      if (addBtn) addBtn.addEventListener('click', function (e) { e.stopPropagation(); changeRefillQty(card, +1); });
      if (minus)  minus.addEventListener('click',  function (e) { e.stopPropagation(); changeRefillQty(card, -1); });
      if (plus)   plus.addEventListener('click',   function (e) { e.stopPropagation(); changeRefillQty(card, +1); });
    });

    // ── Blend accordion ─────────────────────────────────────────────
    var blendHint = document.querySelector('[data-blend-hint]');

    dom.blendTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var key = tab.dataset.blendTab;
        var isOpen = tab.classList.contains('is-open');

        dom.blendTabs.forEach(function (t) {
          t.classList.remove('is-open');
          t.setAttribute('aria-expanded', 'false');
        });
        dom.blendPanels.forEach(function (p) { p.classList.add('is-hidden'); });

        if (!isOpen) {
          tab.classList.add('is-open');
          tab.setAttribute('aria-expanded', 'true');
          var panel = document.querySelector('.spb-v5-blend-panel[data-blend-panel="' + key + '"]');
          if (panel) panel.classList.remove('is-hidden');
          if (blendHint) blendHint.hidden = true;
        }
      });
    });

    // ── Carousel — thumbnail click scrolls the track; swiping the track
    //    keeps the active thumbnail in sync (IntersectionObserver) ──────
    var carouselMain = document.querySelector('.spb-v5-carousel-main');
    if (carouselMain) {
      document.addEventListener('click', function (e) {
        var thumb = e.target.closest('.spb-v5-thumb');
        if (!thumb) return;
        var index = thumb.dataset.v5Thumb;
        var slide = carouselMain.querySelector('.spb-v5-carousel-slide[data-v5-slide="' + index + '"]');
        if (slide) slide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });

      var slides = carouselMain.querySelectorAll('.spb-v5-carousel-slide');
      if (slides.length) {
        var slideObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
            var index = entry.target.dataset.v5Slide;
            document.querySelectorAll('.spb-v5-thumb').forEach(function (t) {
              t.classList.toggle('is-active', t.dataset.v5Thumb === index);
            });
          });
        }, { root: carouselMain, threshold: 0.6 });
        slides.forEach(function (s) { slideObserver.observe(s); });
      }
    }

    // ── Carousel — desktop thumbnail sizing ─────────────────────────────
    // CSS can't do this alone: flex-grow needs a height to size the thumbs,
    // but aspect-ratio needs a size to derive height from — circular, and
    // browsers collapse the whole row when asked to resolve both at once.
    // So: measure the main image's real rendered height, divide it evenly
    // across however many thumbs exist (minus gaps), and size each thumb
    // to that exact square pixel value via inline style.
    var thumbsCol = document.querySelector('.spb-v5-thumbs');
    if (carouselMain && thumbsCol) {
      var desktopMq = window.matchMedia('(min-width: 768px)');

      var sizeThumbs = function () {
        var thumbs = thumbsCol.querySelectorAll('.spb-v5-thumb');
        if (!thumbs.length) return;

        if (!desktopMq.matches) {
          thumbs.forEach(function (t) { t.style.width = ''; t.style.height = ''; });
          return;
        }

        var gap = parseFloat(getComputedStyle(thumbsCol).rowGap || getComputedStyle(thumbsCol).gap) || 0;
        var totalGap = gap * (thumbs.length - 1);
        var size = (carouselMain.clientHeight - totalGap) / thumbs.length;
        if (size <= 0) return;

        thumbs.forEach(function (t) {
          t.style.width = size + 'px';
          t.style.height = size + 'px';
        });
      };

      var sizeThumbsRaf = null;
      var sizeThumbsThrottled = function () {
        if (sizeThumbsRaf) return;
        sizeThumbsRaf = requestAnimationFrame(function () {
          sizeThumbsRaf = null;
          sizeThumbs();
        });
      };

      sizeThumbs();
      window.addEventListener('resize', sizeThumbsThrottled);
      desktopMq.addEventListener('change', sizeThumbs);
    }

    // ── PDP drawer helpers ────────────────────────────────────
    function runInjectedScripts(container) {
      container.querySelectorAll('script').forEach(function (old) {
        var s = document.createElement('script');
        s.textContent = old.textContent;
        old.parentNode.replaceChild(s, old);
      });
    }

    function closePdpDrawer() {
      var drawer   = document.getElementById('fb-pdp-drawer');
      var backdrop = document.getElementById('fb-pdp-backdrop');
      if (drawer)   { drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); }
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.style.overflow = '';
      var panel = document.querySelector('[data-pdp-drawer-nutrition-panel]');
      if (panel) { panel.classList.remove('is-open'); panel.setAttribute('aria-hidden', 'true'); }
    }

    function injectPdpContent(html) {
      var body = document.getElementById('fb-pdp-body');
      if (!body) return;
      body.innerHTML = html;
      runInjectedScripts(body);
    }

    function openPdpDrawer(handle, title) {
      var drawer   = document.getElementById('fb-pdp-drawer');
      var backdrop = document.getElementById('fb-pdp-backdrop');
      var body     = document.getElementById('fb-pdp-body');
      if (body) body.innerHTML = '<p style="padding:2rem;text-align:center;color:var(--color-text-secondary);">Loading&hellip;</p>';
      if (drawer)   { drawer.classList.add('is-open'); drawer.removeAttribute('aria-hidden'); }
      if (backdrop) backdrop.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (pdpCache[handle]) { injectPdpContent(pdpCache[handle]); return; }
      fetch('/products/' + handle + '?view=pdp-drawer-kapsule')
        .then(function (r) { return r.text(); })
        .then(function (html) { pdpCache[handle] = html; injectPdpContent(html); })
        .catch(function () {
          var body = document.getElementById('fb-pdp-body');
          if (body) body.innerHTML = '<p style="padding:2rem;text-align:center;">Unable to load product info.</p>';
        });
    }

    // Event delegation for CTA + PDP drawer
    document.addEventListener('click', function (e) {
      var infoBtn = e.target.closest('[data-pdp-info-open]');
      if (infoBtn) {
        e.stopPropagation();
        var card = infoBtn.closest('.spb-refill-card');
        if (card) openPdpDrawer(card.dataset.handle, card.dataset.title);
        return;
      }
      if (e.target.closest('#fb-pdp-close') || e.target.id === 'fb-pdp-backdrop') {
        closePdpDrawer();
        return;
      }
      var nutBtn = e.target.closest('[data-pdp-drawer-nutrition-open]');
      if (nutBtn) {
        var panel = document.querySelector('[data-pdp-drawer-nutrition-panel]');
        if (panel) { panel.classList.add('is-open'); panel.removeAttribute('aria-hidden'); }
        return;
      }
      if (e.target.closest('[data-pdp-drawer-nutrition-close]')) {
        var panel = document.querySelector('[data-pdp-drawer-nutrition-panel]');
        if (panel) { panel.classList.remove('is-open'); panel.setAttribute('aria-hidden', 'true'); }
        return;
      }
      var ctaBtn = e.target.closest('[data-fb-cta]');
      if (ctaBtn && !ctaBtn.disabled) {
        addToCart();
        return;
      }
      var refillsLink = e.target.closest('[data-scroll-refills]');
      if (refillsLink) {
        e.preventDefault();
        var refillSection = document.getElementById('spb-step-refills');
        if (refillSection) refillSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var panel = document.querySelector('[data-pdp-drawer-nutrition-panel].is-open');
      if (panel) {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
        return;
      }
      var drawer = document.getElementById('fb-pdp-drawer');
      if (drawer && drawer.classList.contains('is-open')) closePdpDrawer();
    });

    // ── Default selection on load — first type card, its first color ──
    if (dom.bottleTypeCards.length) {
      var defaultCard = document.querySelector('.spb-v5-bottle-type-card.is-selected') || dom.bottleTypeCards[0];
      selectBottleType(defaultCard, { skipRender: true });
    }

    render();
  }

  // ── Bottle ───────────────────────────────────────────────────────
  function selectBottleType(card, opts) {
    opts = opts || {};
    dom.bottleTypeCards.forEach(function (c) { c.classList.remove('is-selected'); });
    card.classList.add('is-selected');

    var type = card.dataset.type;
    state.bottleType = type;

    dom.colorRows.forEach(function (row) {
      row.classList.toggle('is-hidden', row.dataset.colorRowFor !== type);
    });

    applyBottleFromCard(card);
    if (!opts.skipRender) render();
  }

  function applyBottleFromCard(card) {
    state.bottleVariantId  = parseInt(card.dataset.variantId, 10);
    state.bottlePrice      = parseInt(card.dataset.price, 10);
    state.bottleTitle      = card.dataset.title      || '';
    state.bottleTypeLabel  = card.dataset.typeLabel   || '';
    state.bottleImage      = card.dataset.image       || '';
    state.bottleColorLabel = card.dataset.subtext     || '';
    state.bottleColorClass = card.dataset.colorClass  || '';
    state.bottleBg         = card.dataset.bg          || '';

    document.querySelectorAll('[data-spb-bottle-type-label]').forEach(function (el) {
      el.textContent = state.bottleTypeLabel;
    });
    document.querySelectorAll('[data-spb-color-label]').forEach(function (el) {
      el.textContent = state.bottleColorLabel;
    });
  }

  // ── Refills (optional add-ons — never gate the CTA) ───────────────
  function changeRefillQty(card, delta) {
    var variantId = card.dataset.variantId;
    var price     = parseInt(card.dataset.price, 10);
    var qtyEl     = card.querySelector('[data-spb-qty]');
    var addBtn    = card.querySelector('[data-spb-add]');
    var qtySel    = card.querySelector('[data-spb-qty-wrap]');

    if (!state.refills[variantId]) {
      state.refills[variantId] = {
        qty:   0,
        price: price,
        title: card.dataset.title || '',
        image: card.dataset.image || '',
        blend: card.dataset.blend || '',
      };
    }

    var newQty = Math.max(0, state.refills[variantId].qty + delta);
    state.refills[variantId].qty = newQty;
    if (qtyEl) qtyEl.textContent = newQty;

    if (newQty > 0) {
      card.classList.add('is-active');
      if (addBtn) addBtn.hidden = true;
      if (qtySel) qtySel.hidden = false;
    } else {
      card.classList.remove('is-active');
      if (addBtn) addBtn.hidden = false;
      if (qtySel) qtySel.hidden = true;
      delete state.refills[variantId];
    }

    render();
  }

  function removeItem(type, variantId) {
    if (type === 'refill') {
      var card = document.querySelector('.spb-refill-card[data-variant-id="' + variantId + '"]');
      if (card) {
        card.classList.remove('is-active');
        var qtyEl  = card.querySelector('[data-spb-qty]');
        var addBtn = card.querySelector('[data-spb-add]');
        var qtySel = card.querySelector('[data-spb-qty-wrap]');
        if (qtyEl)  qtyEl.textContent = '0';
        if (addBtn) addBtn.hidden = false;
        if (qtySel) qtySel.hidden = true;
      }
      delete state.refills[variantId];
    }
    render();
  }

  // ── Helpers ──────────────────────────────────────────────────────
  function getRefillsSubtotal() {
    return Object.values(state.refills).reduce(function (s, r) { return s + r.price * r.qty; }, 0);
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ── Render ───────────────────────────────────────────────────────
  function render() {
    renderSummary();
    renderOrderCard();
    renderMobileFooter();
    if (dom.section) dom.section.classList.add('is-ready');
  }

  // ── Summary list (inline inside the order card) ────────────────────
  function renderSummary() {
    if (!dom.summaryList) return;

    if (dom.summaryInline) dom.summaryInline.hidden = false;

    var hasRefills = Object.keys(state.refills).length > 0;

    var html = '';
    html += lineItemRow('bottle', state.bottleVariantId, state.bottleTitle, state.bottleColorLabel, state.bottleImage, state.bottlePrice, 1);

    if (!hasRefills) {
      html += '<a href="#spb-step-refills" class="spb-summary-refill-prompt" data-scroll-refills>Add refills and save 15% today</a>';
    }

    Object.entries(state.refills).forEach(function (entry) {
      var id = entry[0], r = entry[1];
      if (r.qty > 0) html += lineItemRow('refill', id, r.title, '', r.image, r.price, r.qty);
    });

    dom.summaryList.innerHTML = html;

    dom.summaryList.querySelectorAll('.spb-line-remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var row = btn.closest('.spb-line-item');
        if (row) removeItem(row.dataset.removeType, row.dataset.removeId);
      });
    });
  }

  // ── Single order card (price + CTA) ───────────────────────────────
  function renderOrderCard() {
    var subtotalRaw = state.bottlePrice + getRefillsSubtotal();
    var discounted  = Math.round(subtotalRaw * DISCOUNT);

    var priceEl = document.querySelector('[data-fb-plan-price="order"]');
    if (priceEl) {
      priceEl.innerHTML = subtotalRaw > 0
        ? '<span class="fb-plan-row-price--sub">' + formatMoney(discounted) + '</span>' +
          ' <span class="fb-plan-row-price-compare">' + formatMoney(subtotalRaw) + '</span>'
        : '—';
    }

    document.querySelectorAll('[data-fb-cta="order"]').forEach(function (cta) {
      var isMobile  = cta.classList.contains('fb-mobile-cta');
      var ctaLabel  = cta.dataset.ctaText || 'Pre-Order Now';
      cta.disabled  = false;

      if (isMobile) {
        cta.innerHTML = subtotalRaw > 0
          ? ctaLabel + '  ' + formatMoney(discounted) +
            ' <span class="fb-mobile-cta__compare">' + formatMoney(subtotalRaw) + '</span>'
          : ctaLabel;
      } else {
        cta.textContent = ctaLabel;
      }
    });
  }

  // ── Mobile sticky footer ──────────────────────────────────────────
  function renderMobileFooter() {
    if (!dom.mobileFooter) return;

    if (dom.mobileBottleLabel) {
      dom.mobileBottleLabel.textContent = state.bottleColorLabel + ' ' + state.bottleTypeLabel;
    }

    if (dom.mobileBottleSwatch) {
      dom.mobileBottleSwatch.style.background = state.bottleBg;
      dom.mobileBottleSwatch.hidden = false;
    }

    if (dom.mobileRefillsRow) dom.mobileRefillsRow.hidden = false;

    if (dom.mobileRefillsText) {
      var refillQty = Object.values(state.refills).reduce(function (s, r) { return s + r.qty; }, 0);
      dom.mobileRefillsText.textContent = refillQty > 0
        ? '+' + refillQty + ' refill' + (refillQty !== 1 ? 's' : '')
        : 'Add refills or';
    }
  }

  // ── Line item row ────────────────────────────────────────────────
  function lineItemRow(type, variantId, title, subtext, image, unitPrice, qty) {
    var linePrice = unitPrice * qty;
    var discPrice = Math.round(linePrice * DISCOUNT);
    var label     = type === 'bottle' ? title : qty + 'x ' + title;
    var safeLabel = label.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    var safeTitle = title.replace(/"/g, '&quot;');
    var safeSub   = subtext ? subtext.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';

    var thumbHtml = image
      ? '<img class="spb-line-thumb" src="' + image + '" alt="' + safeTitle + '" loading="lazy">'
      : '<div class="spb-line-thumb"></div>';

    var displaySub = safeSub;
    var subExtraClass = '';
    if (type === 'refill') {
      var totalKapsules = qty * SERVINGS_PER_REFILL;
      var perKapsule = Math.round(unitPrice * DISCOUNT) / 100 / SERVINGS_PER_REFILL;
      subExtraClass = ' spb-line-sub--green';
      displaySub = totalKapsules + ' Kapsules - $' + perKapsule.toFixed(2) + ' each';
    }
    var subtextHtml = displaySub ? '<p class="spb-line-sub text-meta' + subExtraClass + '">' + displaySub + '</p>' : '';

    var priceHtml = '<div class="spb-line-price">' +
        '<span class="spb-line-price-main">' + formatMoney(discPrice) + '</span>' +
        '<span class="spb-line-price-compare">' + formatMoney(linePrice) + '</span>' +
      '</div>';

    var removeHtml = type !== 'bottle'
      ? '<button type="button" class="spb-line-remove" aria-label="Remove ' + safeTitle + '">' +
          (dom.removeIconSvg || '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 4h10"/><path d="M6 4V2h4v2"/><path d="M5 4l1 9h4l1-9"/></svg>') +
        '</button>'
      : '';

    var bgAttr = (type === 'bottle' && state.bottleBg) ? ' style="background: ' + state.bottleBg + ';"' : '';

    return (
      '<div class="spb-line-item"' + bgAttr + ' data-remove-type="' + type + '" data-remove-id="' + variantId + '">' +
        thumbHtml +
        '<div class="spb-line-info">' +
          '<p class="spb-line-name">' + safeLabel + '</p>' +
          subtextHtml +
        '</div>' +
        '<div class="spb-line-right">' + priceHtml + removeHtml + '</div>' +
      '</div>'
    );
  }

  // ── Add to cart → /checkout ───────────────────────────────────────
  function addToCart() {
    document.querySelectorAll('[data-fb-cta]').forEach(function (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    });

    var existingBanner = document.getElementById('fb-atc-error');
    if (existingBanner) existingBanner.textContent = '';

    var items = [{
      id:         state.bottleVariantId,
      quantity:   1,
      properties: {},
    }];

    Object.entries(state.refills).forEach(function (entry) {
      var variantId = entry[0], r = entry[1];
      if (r.qty < 1) return;
      items.push({
        id:         parseInt(variantId, 10),
        quantity:   r.qty,
        properties: {},
      });
    });

    var flavorSummary = Object.values(state.refills)
      .filter(function (r) { return r.qty > 0; })
      .map(function (r) { return r.title + ' x' + r.qty; })
      .join(', ');

    var cartAttributes = {
      '_flavors': flavorSummary,
    };

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var doAdd = function () {
          return fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ items: items }),
          });
        };
        return cart.item_count > 0
          ? fetch('/cart/clear.js', { method: 'POST' }).then(doAdd)
          : doAdd();
      })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      })
      .then(function () {
        return fetch('/cart/update.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ attributes: cartAttributes }),
        });
      })
      .then(function () {
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        syncCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
        var url = '/discount/' + DISCOUNT_PCT + '?redirect=' + encodeURIComponent('/discount/' + DISCOUNT_SHIP + '?redirect=/checkout');
        window.location.href = url;
      })
      .catch(function (err) {
        console.error('[presale-builder-v5] ATC error:', err);

        var msg = 'Something went wrong — please try again.';
        if (err && err.message && err.message.indexOf('422') !== -1) {
          msg = 'One or more items are unavailable. Please refresh and try again.';
        }

        var banner = document.getElementById('fb-atc-error');
        if (!banner) {
          banner = document.createElement('p');
          banner.id = 'fb-atc-error';
          banner.style.cssText = 'color:#c0392b;font-size:0.95rem;margin:0.75rem 0 0;text-align:center;';
          var ctaWrap = document.querySelector('.fb-cta-wrap');
          if (ctaWrap) ctaWrap.appendChild(banner);
        }
        banner.textContent = msg;

        render();
      });
  }

  // ── Cart count sync ───────────────────────────────────────────────
  function syncCartCount(count) {
    var cartLink = document.querySelector('.header__icon--cart');
    if (!cartLink) return;

    var bubble = cartLink.querySelector('.cart-count-bubble');

    if (count > 0) {
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML =
          '<span aria-hidden="true"></span>' +
          '<span class="visually-hidden">' + count + ' item' + (count === 1 ? '' : 's') + '</span>';
        cartLink.appendChild(bubble);
      }
      var countEl = bubble.querySelector('[aria-hidden="true"]');
      if (countEl) countEl.textContent = count;
    } else {
      if (bubble) bubble.remove();
    }
  }

  // ── Boot ─────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', function () {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { syncCartCount(cart.item_count); });
  });

})();
